import { useMemo } from 'react';
import { SwapSDK } from '@chainflip/sdk/swap';
import { type ChainflipAsset, type ChainflipChain } from '@chainflip/utils/chainflip';
import { useMutation } from '@tanstack/react-query';
import { type ChainflipToken } from '@/shared/assets/tokens';
import { usePolkadot } from '@/shared/hooks/usePolkadot';
import useTracking from '@/shared/hooks/useTracking';
import { getKnownChainflipNetwork } from '@/shared/utils/env';
import usePendingDeposits from './usePendingDeposits';
import { useExtrinsicSDK, useStateChainAccount } from './useStateChainAccount';
import useStore from './useStore';
import { LiquidityProvisionEvents, type LiquidityProvisionTrackEvents } from '../types/track';

type ChannelInfo = {
  depositChainExpiryBlock: number;
  channelId: number;
  boostFee: number;
  asset: ChainflipToken;
  depositAddress: string;
  blockNumber: number;
};

export default function useDepositChannel({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: () => void;
}) {
  const addChannel = useStore((state) => state.addChannel);
  const pendingDeposits = usePendingDeposits();
  const { selectedAccount: polkadotAccount } = usePolkadot();
  const { accountInfo } = useStateChainAccount();
  const { sendExtrinsic, toast: channelToast, getExtrinsicSDK } = useExtrinsicSDK('openChannel');
  const track = useTracking<LiquidityProvisionTrackEvents>();
  const storeRefundAddresses = useStore((state) => state.refundAddresses);

  const mutationFn =
    polkadotAccount?.type === 'evm' && accountInfo?.role !== 'liquidity_provider'
      ? async ({
          token,
          boostFeeBps = 0,
        }: {
          token: ChainflipToken;
          boostFeeBps?: number;
        }): Promise<ChannelInfo> => {
          const refundAddress = storeRefundAddresses[token.chain.chainflipId as ChainflipChain];

          if (!refundAddress) {
            throw new Error('Refund address not found for the selected asset.');
          }

          const payload = await getExtrinsicSDK().signAccountCreationPayload(
            token.chainflipId as ChainflipAsset,
            refundAddress,
          );

          const sdk = new SwapSDK({
            network: getKnownChainflipNetwork(),
            rpcUrl: process.env.NEXT_PUBLIC_STATECHAIN_RPC_HTTP_URL,
          });

          const result = await sdk.requestAccountCreationDepositAddress(
            token.chainflipId,
            payload.refundAddress,
            payload.signatureData,
            payload.transactionMetadata,
            boostFeeBps,
          );

          const [, , channelId] = result.id.split('-');

          return {
            depositChainExpiryBlock: Number(result.srcChainExpiryBlock),
            channelId: Number(channelId),
            boostFee: result.maxBoostFeeBps,
            asset: token,
            depositAddress: result.depositAddress,
            blockNumber: result.issuedBlock,
          };
        }
      : ({ token, boostFeeBps = 0 }: { token: ChainflipToken; boostFeeBps?: number }) =>
          sendExtrinsic(token.chainflipId, boostFeeBps, () => {
            track(LiquidityProvisionEvents.OpenDepositChannel, {
              props: {
                path: window.location.pathname,
                scAccount: polkadotAccount?.idSs58 ?? '-',
                position: 'deposit_asset_modal',
                asset: token.symbol,
                boostFeeBps,
              },
            });
            channelToast.loading('Submitting transaction to the State Chain...');
          });

  const {
    mutate: createDepositChannel,
    reset: resetDepositChannelState,
    data: depositChannel,
    isPending: depositChannelLoading,
  } = useMutation({
    mutationFn,
    onSuccess: (channel) => {
      track(LiquidityProvisionEvents.OpenDepositChannelSuccess, {
        props: {
          path: window.location.pathname,
          scAccount: polkadotAccount?.idSs58 ?? '-',
          position: 'deposit_asset_modal',
          asset: channel.asset.symbol,
          depositAddress: channel.depositAddress,
          boostFeeBps: channel.boostFee,
        },
      });
      channelToast.success('Deposit channel opened successfully');
      addChannel(channel);
      onSuccess?.();
    },
    onError: (err) => {
      channelToast.error('Error while submitting transaction:', {
        description: err.message,
      });
      onError?.();
    },
  });

  const pendingChannelDeposits = useMemo(() => {
    if (!depositChannel) return [];

    return (pendingDeposits.get(depositChannel.asset.chainflipId) ?? []).filter(
      (deposit) => deposit.depositAddress === depositChannel.depositAddress,
    );
  }, [pendingDeposits, depositChannel]);

  return {
    createDepositChannel,
    resetDepositChannelState,
    depositChannel,
    depositChannelLoading,
    pendingDeposits: pendingChannelDeposits,
  };
}
